import { motion } from 'framer-motion';
import { Thermometer, Clock, Snowflake, Sun, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export default function CakeCarePage() {
  const tips = [
    {
      icon: Thermometer,
      title: 'Storage Temperature',
      description: 'Store your cake in a cool place away from direct sunlight. Room temperature (18-22°C) is ideal for most cakes.',
      color: 'bg-blue-100 text-blue-800'
    },
    {
      icon: Snowflake,
      title: 'Refrigeration',
      description: 'Cakes with fresh cream or cream cheese frosting should be refrigerated. Remove 30 minutes before serving.',
      color: 'bg-cyan-100 text-cyan-800'
    },
    {
      icon: Clock,
      title: 'Best Enjoyed Fresh',
      description: 'Our cakes are best consumed within 3 days. Buttercream cakes last longer than fresh cream cakes.',
      color: 'bg-amber-100 text-amber-800'
    },
    {
      icon: Sun,
      title: 'Serving Tips',
      description: 'For the best flavor and texture, serve cakes at room temperature. Cold cakes can taste dense.',
      color: 'bg-orange-100 text-orange-800'
    }
  ];

  const detailedGuides = [
    {
      title: 'Buttercream Cakes',
      storage: 'Can be stored at room temperature for up to 3 days in a cool place.',
      refrigeration: 'If refrigerated, bring to room temperature 30-60 minutes before serving.',
      cutting: 'Use a sharp knife dipped in hot water for clean cuts.'
    },
    {
      title: 'Fresh Cream Cakes',
      storage: 'Must be refrigerated and consumed within 2 days.',
      refrigeration: 'Keep in the refrigerator at 4°C. Remove 20-30 minutes before serving.',
      cutting: 'Cut while still slightly chilled for neater slices.'
    },
    {
      title: 'Fondant Cakes',
      storage: 'Store at room temperature. Avoid refrigeration as moisture can damage fondant.',
      refrigeration: 'Only refrigerate if absolutely necessary; the fondant may become sticky.',
      cutting: 'Score the fondant first before cutting through the cake.'
    },
    {
      title: 'Cupcakes',
      storage: 'Store in an airtight container at room temperature for up to 2 days.',
      refrigeration: 'Refrigerate cream-topped cupcakes. Buttercream ones are fine at room temp.',
      cutting: 'N/A - Enjoy whole!'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cake-care-page">
      {/* Header */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="font-handwritten text-2xl text-[#8D6E63]">Expert Advice</span>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1a1a1a] mt-2">
              Cake Care Tips
            </h1>
            <p className="text-stone-600 mt-4 max-w-2xl">
              To ensure you enjoy your Mad Brothers bakes at their best, follow these storage and serving guidelines.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tips.map((tip, idx) => (
              <motion.div
                key={tip.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm text-center"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${tip.color}`}>
                  <tip.icon className="w-7 h-7" />
                </div>
                <h3 className="font-semibold text-[#1a1a1a] mb-2">{tip.title}</h3>
                <p className="text-stone-500 text-sm">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Guides */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1a1a1a]">
              Care by Cake Type
            </h2>
            <p className="text-stone-600 mt-2">Specific guidelines for each type of cake</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {detailedGuides.map((guide, idx) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-stone-50 rounded-2xl p-6"
              >
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1a1a1a] mb-4">
                  {guide.title}
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-[#8D6E63] mb-1">Storage</h4>
                    <p className="text-stone-600 text-sm">{guide.storage}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#8D6E63] mb-1">Refrigeration</h4>
                    <p className="text-stone-600 text-sm">{guide.refrigeration}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[#8D6E63] mb-1">Cutting Tips</h4>
                    <p className="text-stone-600 text-sm">{guide.cutting}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-[#8D6E63]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-[#8D6E63]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1a1a1a] mb-2">Need More Help?</h3>
              <p className="text-stone-600 mb-4">
                If you have specific questions about caring for your cake or encounter any issues, 
                please don't hesitate to reach out. We're always happy to help!
              </p>
              <Link to="/contact">
                <Button className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
